import { useState } from "react";
import { Plus, GripVertical, Pencil, Trash2, X, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { WorkspaceProjectColumn } from "@/hooks/useWorkspaceProjects";

const COLUMN_COLORS = [
  { name: "Azul", value: "bg-blue-500" },
  { name: "Verde", value: "bg-green-500" },
  { name: "Amarelo", value: "bg-yellow-500" },
  { name: "Laranja", value: "bg-orange-500" },
  { name: "Vermelho", value: "bg-red-500" },
  { name: "Roxo", value: "bg-purple-500" },
  { name: "Rosa", value: "bg-pink-500" },
  { name: "Ciano", value: "bg-cyan-500" },
  { name: "Cinza", value: "bg-gray-500" },
  { name: "Índigo", value: "bg-indigo-500" },
];

interface ProjectColumnManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectName?: string;
  columns: WorkspaceProjectColumn[];
  loading?: boolean;
  onCreate: (input: { name: string; color: string }) => Promise<unknown>;
  onUpdate: (
    id: string,
    patch: Partial<Pick<WorkspaceProjectColumn, "name" | "color">>,
  ) => Promise<boolean>;
  onDelete: (id: string) => Promise<boolean>;
}

export function ProjectColumnManagerDialog({
  open,
  onOpenChange,
  projectName,
  columns,
  loading = false,
  onCreate,
  onUpdate,
  onDelete,
}: ProjectColumnManagerDialogProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(COLUMN_COLORS[0].value);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");

  const handleCreate = async () => {
    if (!newName.trim()) return;
    const result = await onCreate({ name: newName.trim(), color: newColor });
    if (result) {
      setNewName("");
      setNewColor(COLUMN_COLORS[0].value);
      setIsCreating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editName.trim()) return;
    const ok = await onUpdate(editingId, { name: editName.trim(), color: editColor });
    if (ok) {
      setEditingId(null);
      setEditName("");
      setEditColor("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Colunas do Projeto</DialogTitle>
          <DialogDescription>
            Gerencie as colunas Kanban para {projectName || "este projeto"}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {loading ? (
              <div className="text-center py-4 text-muted-foreground">Carregando...</div>
            ) : columns.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                Nenhuma coluna cadastrada.
              </div>
            ) : (
              columns.map((col) => (
                <div key={col.id} className="flex items-center gap-2 p-2 rounded-lg border bg-card">
                  {editingId === col.id ? (
                    <div className="flex-1 space-y-2">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="h-8"
                        autoFocus
                      />
                      <div className="flex flex-wrap gap-1.5">
                        {COLUMN_COLORS.map((color) => (
                          <button
                            key={color.value}
                            type="button"
                            onClick={() => setEditColor(color.value)}
                            className={cn(
                              "w-5 h-5 rounded-full transition-all",
                              color.value,
                              editColor === color.value
                                ? "ring-2 ring-offset-2 ring-primary"
                                : "hover:scale-110",
                            )}
                            title={color.name}
                          />
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => void handleSaveEdit()} className="h-7">
                          <Check className="w-3 h-3 mr-1" />
                          Salvar
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-7">
                          <X className="w-3 h-3 mr-1" />
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
                      <div className={cn("w-3 h-3 rounded-full shrink-0", col.color)} />
                      <span className="flex-1 text-sm font-medium truncate">{col.name}</span>
                      {!col.is_system && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => {
                              setEditingId(col.id);
                              setEditName(col.name);
                              setEditColor(col.color);
                            }}
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive"
                            onClick={() => void onDelete(col.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </div>
              ))
            )}
          </div>

          {isCreating ? (
            <div className="space-y-3 p-3 border rounded-lg">
              <Input
                placeholder="Nome da coluna"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                autoFocus
              />
              <div className="flex flex-wrap gap-1.5">
                {COLUMN_COLORS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setNewColor(color.value)}
                    className={cn(
                      "w-6 h-6 rounded-full transition-all",
                      color.value,
                      newColor === color.value ? "ring-2 ring-offset-2 ring-primary" : "hover:scale-110",
                    )}
                    title={color.name}
                  />
                ))}
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => void handleCreate()} disabled={!newName.trim()}>
                  Criar coluna
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setIsCreating(false)}>
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" className="w-full" onClick={() => setIsCreating(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Adicionar coluna
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
